<template>
    <div class="form__input-container">
      <fieldset class="form__field">
        <label :for="modelPath" class="form__label tooltip" v-if="label">
          {{ L.localize(label) }}<span class="tooltiptext" v-if="hasTooltip">{{ L.localize(tooltip) }}</span>
        </label>

        <DatePicker
          :value="value"
          :type="type"
          v-if="type === 'date' || type == 'datetime'"
          :data-acc="acc.toLowerCase()"
          @input="updateValue" />

        <input
          class="form__input"
          :class="classes"
          ref="input"

          :autocomplete="autocomplete ? autocomplete : null"
          :disabled="disabled"
          :max="maxValue"
          :maxlength="enforceMaxlength ? maxlength : null"
          :min="minValue"
          :name="name"
          :data-acc="acc.toLowerCase()"
          :number="type === 'number' ? true : null"
          :placeholder="hasFloatingLabel ? null : L.localize(placeholder)"
          :readonly="readonly"
          :required="required"
          :step="stepValue"
          :type="type"
          :value="value"

          @blur="onBlur"
          @change="onChange"
          @focus="onFocus"
          @input="updateValue($event.target.value)"
          @keydown.enter="onKeydownEnter"
          @keydown="onKeydown"
          v-else-if="!multiLine"
        >

        <textarea
          class="form__input"
          ref="textarea"

          :autocomplete="autocomplete ? autocomplete : null"
          :disabled="disabled"
          :maxlength="enforceMaxlength ? maxlength : null"
          :name="name"
          :placeholder="hasFloatingLabel ? null : placeholder"
          :readonly="readonly"
          :required="required"
          :rows="rows"
          :value="value"
          :data-acc="acc.toLowerCase()"

          @blur="onBlur"
          @change="onChange"
          @focus="onFocus"
          @input="updateValue($event.target.value)"
          @keydown.enter="onKeydownEnter"
          @keydown="onKeydown"
          v-else
        >{{ value }}</textarea>

        <validation v-if="validate" :validate="validate[modelPath]" :model="modelPath"></validation>
      </fieldset>

        <div class="ui-textbox__content">

            <div class="ui-textbox__feedback" v-if="hasFeedback || maxlength">
                <div class="ui-textbox__feedback-text" v-if="showError">
                    <slot name="error">{{ error }}</slot>
                </div>

                <div class="ui-textbox__feedback-text" v-else-if="showHelp">
                    <slot name="help">{{ help }}</slot>
                </div>

                <div class="ui-textbox__counter" v-if="maxlength">
                    {{ value.length + '/' + maxlength }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from 'components/layout/forms/DatePicker'
import L from 'services/Localization'
import { Validation } from 'components/helpers'
import autosize from 'autosize'

export default {
  name: 'textbox',
  components: { Validation, DatePicker },
  props: {
    name: String,
    placeholder: String,
    tooltip: String,
    value: {
      type: [String, Number],
      required: false
    },
    acc: {
      type: String,
      required: false,
      default: ''
    },
    modelPath: {
      type: String
    },
    label: String,
    floatingLabel: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    multiLine: {
      type: Boolean,
      default: false
    },
    rows: {
      type: Number,
      default: 2
    },
    autocomplete: String,
    autosize: {
      type: Boolean,
      default: true
    },
    min: Number,
    max: Number,
    step: {
      type: String,
      default: 'any'
    },
    maxlength: Number,
    enforceMaxlength: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    help: String,
    error: String,
    invalid: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    validate: {
      type: Object
    }
  },

  data() {
    return {
      isActive: false,
      isTouched: false,
      initialValue: this.value,
      autosizeInitialized: false,
      L: L
    }
  },

  computed: {
    classes() {
      return [
        { 'is-active': this.isActive },
        { 'is-invalid': this.invalid },
        { 'is-touched': this.isTouched },
        { 'is-multi-line': this.multiLine },
        { 'has-counter': this.maxlength },
        { 'is-disabled': this.disabled },
        { 'has-label': this.hasLabel },
        { 'has-floating-label': this.hasFloatingLabel },
        { 'form__input--number': this.type === 'number' }
      ]
    },

    labelClasses() {
      return {
        'is-inline': this.hasFloatingLabel && this.isLabelInline,
        'is-floating': this.hasFloatingLabel && !this.isLabelInline
      }
    },

    hasLabel() {
      return Boolean(this.label) || Boolean(this.$slots.default)
    },

    hasFloatingLabel() {
      return this.hasLabel && this.floatingLabel
    },

    isLabelInline() {
      return this.value.length === 0 && !this.isActive
    },

    minValue() {
      if (this.type === 'number' && this.min !== undefined) {
        return this.min
      }

      return null
    },

    maxValue() {
      if (this.type === 'number' && this.max !== undefined) {
        return this.max
      }

      return null
    },

    stepValue() {
      return this.type === 'number' ? this.step : null
    },

    hasFeedback() {
      return Boolean(this.help) || Boolean(this.error)
    },

    showError() {
      return this.invalid && Boolean(this.error)
    },

    showHelp() {
      return !this.showError && Boolean(this.help)
    },

    hasTooltip() {
      return this.tooltip !== undefined
    }
  },

  mounted() {
    if (this.multiLine && this.autosize) {
      autosize(this.$refs.textarea)
      this.autosizeInitialized = true
    }
  },

  beforeDestroy() {
    if (this.autosizeInitialized) {
      autosize.destroy(this.$refs.textarea)
    }
  },

  methods: {
    updateValue(value) {
      this.$emit('input', value)
    },

    onChange(e) {
      this.$emit('change', this.value, e)
    },

    onFocus(e) {
      this.isActive = true
      this.$emit('focus', e)
    },

    onBlur(e) {
      this.isActive = false
      this.$emit('blur', e)

      if (!this.isTouched) {
        this.isTouched = true
        this.$emit('touch')
      }
    },

    onKeydown(e) {
      this.$emit('keydown', e)
    },

    onKeydownEnter(e) {
      this.$emit('keydown-enter', e)
    },

    reset() {
      // Blur the input if it's focused to prevent required errors
      // when it's value is reset
      if (
        document.activeElement === this.$refs.input ||
        document.activeElement === this.$refs.textarea
      ) {
        document.activeElement.blur()
      }

      this.updateValue(this.initialValue)
      this.resetTouched()
    },

    resetTouched(options = { touched: false }) {
      this.isTouched = options.touched
    },

    refreshSize() {
      if (this.autosizeInitialized) {
        autosize.update(this.$refs.textarea)
      }
    }
  }
}
</script >



// WEBPACK FOOTER //
// TextBox.vue?ee84be98